import { Fragment, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { warnDuplicateSinistro } from "../../lib/checkSinistro";

import { formatDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Checkbox } from "../../components/ui/checkbox";
import PageHeader from "../../components/PageHeader";
import MessageThread from "../../components/MessageThread";
import GenerateLetterDialog from "../../components/GenerateLetterDialog";
import AnagAutocomplete from "../../components/AnagAutocomplete";
import SubjectPicker from "../../components/SubjectPicker";
import CompagniaField from "../../components/CompagniaField";
import { fetchTipologie, asSel, asColorMap, contrastText, DEFAULT_TIPOLOGIE_SINISTRO } from "../../lib/tipologieSinistro";
import SubjectSelect from "../../components/SubjectSelect";
import ProSelect from "../../components/ProSelect";
import SegnalatoreTrendChart from "../../components/SegnalatoreTrendChart";
import ProField from "../../components/ProField";
import NuovoProfessionistaDialog from "../../components/NuovoProfessionistaDialog";
import SalvaDinamicaDialog from "../../components/SalvaDinamicaDialog";
import { PRO_FIELDS, PRO_ATYPES } from "../../lib/proFields";
import { displayName } from "../../lib/anagConfig";
import { defTipo, defLabel } from "../../lib/definita";
import PraticaDetail from "./PraticaDetail";
import NuovaPraticaWizard from "../../components/NuovaPraticaWizard";
import DateInput from "../../components/DateInput";
import { formatNominativo } from "../../lib/nominativo";
import { FolderKanban, Plus, Loader2, FileText, Trash2, Pencil, Sparkles, Link2, Archive, ChevronUp, ChevronDown, MailCheck } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";

import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { isNonAderente, useNonAderenti } from "../../lib/convenzione";

const STATI = [
  { v: "aperta", l: "Aperta" },
  { v: "in_lavorazione", l: "In Lavorazione" },
  { v: "legale", l: "Legale" },
  { v: "mediazione", l: "Mediazione" },
  { v: "chiusa", l: "Chiusa" },
  { v: "out", l: "S.S." },
];

const AUTORITA = ["Polizia di Stato", "Carabinieri", "Polizia Locale", "Guardia di Finanza", "Nessuna", "Altra"];
const RESPONSO_DPR = [
  "Tamponamento — torto 100%",
  "Tamponamento — ragione 100%",
  "Cambio di corsia — torto 100%",
  "Cambio di corsia — ragione 100%",
  "Scontro senza cambio corsia — concorso 50%",
  "Scontro frontale (invasione asse mediano) — torto 100%",
  "Scontro frontale (entrambi invadono) — concorso 50%",
  "Immissione da sosta/area privata — torto 100%",
  "Immissione da sosta/area privata — ragione 100%",
  "Intersezione (mancata precedenza) — torto 100%",
  "Intersezione (mancata precedenza) — ragione 100%",
  "Retromarcia/inversione — torto 100%",
  "Apertura portiera — torto 100%",
  "Concorso paritario 50% / 50%",
  "Concorso 30% / 70%",
  "Concorso 70% / 30%",
  "In attesa di responso",
  "Altro",
];
const STATO_STYLE = {
  aperta: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  in_lavorazione: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  legale: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  mediazione: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  chiusa: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  out: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};
const EMPTY = {
  numero_pratica: "", nome_assistito: "", codice_fiscale: "", targa: "", compagnia: "",
  data_sinistro: "", ora_sinistro: "", stato: "aperta", importo_risarcimento: "", cliente_id: "none",
  descrizione_sinistro: "", note: "", tipo: "", luogo_sinistro: "", indirizzo_sinistro: "", autorita_intervenute: "",
  fornitore_pratica: "", pratica_connessa: "", utente: "", segnalatore: "", veicoli_coinvolti: "", stato_aggiuntivo: "", responso_dpr: "",
  assistito_ragione_sociale: "", assistito_ruolo: "", assistito_ispettorato: "", assistito_num_sinistro: "",
  assistito_liquidatore: "", assistito_perito_compagnia: "", assistito_perito_parte: "", assistito_medico_legale: "",
  assistito_accertatore: "", assistito_riparatore: "", assistito_legale: "", assistito_istruttore: "",
  controparte_ragione_sociale: "", controparte_ruolo: "", controparte_targa: "", controparte_polizza: "",
  controparte_compagnia: "", controparte_ispettorato: "", controparte_num_sinistro: "", controparte_medico_legale: "",
};

const assistitoName = (p) => {
  if (p.assistito_ragione_sociale) return p.assistito_ragione_sociale;
  if (p.nome_assistito) return p.nome_assistito;
  if (p.cliente_nome) return p.cliente_nome;
  const a = Array.isArray(p.assistiti) ? p.assistiti[0] : null;
  if (a) return formatNominativo(a) || a.ragione_sociale || "";
  return "";
};

const clientCompany = (p) => p.compagnia || p.assistito_compagnia || p.assistiti?.[0]?.assicurazione || p.assistiti?.[0]?.compagnia || "";
const controparteCompany = (p) => p.controparti?.[0]?.compagnia || p.controparte_compagnia || "";
const isRDEff = (p) => (p.tipo || "").toUpperCase() === "RD" && !isNonAderente(controparteCompany(p));
const gestionariaCompany = (p) => (isRDEff(p) ? clientCompany(p) : controparteCompany(p));
const gestionariaNumSinistro = (p) => (isRDEff(p) ? (p.assistito_num_sinistro || "") : (p.controparti?.[0]?.num_sinistro || p.controparte_num_sinistro || ""));
const foldText = (s) => String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
function Hl({ text, terms }) {
  const s = String(text ?? "");
  if (!terms?.length || !s) return s;
  const chars = Array.from(s);
  const folded = chars.map((c) => foldText(c) || c);
  const flat = folded.join("");
  const owner = folded.flatMap((f, i) => Array.from(f).map(() => i));
  const mark = new Array(chars.length).fill(false);
  for (const t of terms) {
    let from = 0, at;
    while (t && (at = flat.indexOf(t, from)) >= 0) {
      for (let k = at; k < at + t.length; k++) mark[owner[k]] = true;
      from = at + t.length;
    }
  }
  if (!mark.some(Boolean)) return s;
  const out = [];
  chars.forEach((c, i) => {
    const last = out[out.length - 1];
    if (last && last.m === mark[i]) last.t += c; else out.push({ m: mark[i], t: c });
  });
  return out.map((o, i) => (o.m ? <mark key={i} className="bg-yellow-300 text-slate-900 rounded-sm px-px">{o.t}</mark> : <span key={i}>{o.t}</span>));
}

const SKIP_KEY = /(^_?id$|_id$|_ids$|_at$|^token|password|hash|logo|base64|thumb)/i;
const collectSearchText = (v, key = "", out = []) => {
  if (v == null || SKIP_KEY.test(key)) return out;
  if (typeof v === "string") { if (!v.startsWith("data:")) out.push(v); }
  else if (typeof v === "number") out.push(String(v));
  else if (Array.isArray(v)) v.forEach((x) => collectSearchText(x, key, out));
  else if (typeof v === "object") Object.entries(v).forEach(([k, x]) => collectSearchText(x, k, out));
  return out;
};



// Icona "messa in mora PEC inviata" con tooltip data/ora/destinatari.
function MoraPecIcon({ m, id }) {
  const d = m.at ? new Date(m.at) : null;
  const when = d && !isNaN(d) ? `${d.toLocaleDateString("it-IT")} ore ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : "—";
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 cursor-help" data-testid={`mora-pec-icon-${id}`}>
            <MailCheck className="h-3.5 w-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs" data-testid={`mora-pec-tooltip-${id}`}>
          <div className="font-semibold mb-0.5">Messa in mora inviata via PEC</div>
          {m.nome && <div className="text-[11px] opacity-80 break-words">{m.nome}</div>}
          <div>{when}</div>
          <div className="break-all">A: {(m.to || []).join(", ") || "—"}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}


export default function Pratiche() {
  const { user } = useAuth();
  useNonAderenti();
  const canTrash = user && (user.role === "titolare" || user.role === "superadmin");
  const isAdmin = user && (user.role === "titolare" || user.role === "amministratore");
  const [renumber, setRenumber] = useState(null);
  const [renumberVal, setRenumberVal] = useState("");
  const [renumberSaving, setRenumberSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [rows, setRows] = useState([]);
  const [tipiSel, setTipiSel] = useState(asSel(DEFAULT_TIPOLOGIE_SINISTRO));
  const [tipiColors, setTipiColors] = useState({});
  useEffect(() => { fetchTipologie().then((l) => { setTipiSel(asSel(l)); setTipiColors(asColorMap(l)); }); }, []);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [query, setQuery] = useState("");
  const [fTipo, setFTipo] = useState("all");
  const [fStato, setFStato] = useState("all");
  const [fAnno, setFAnno] = useState("all");
  const [fSeg, setFSeg] = useState("all");
  const [sortKey, setSortKey] = useState(() => localStorage.getItem("pgp_pratiche_sortkey") || null);
  const [sortDir, setSortDir] = useState(() => localStorage.getItem("pgp_pratiche_sortdir") || "asc");
  useEffect(() => {
    if (sortKey) localStorage.setItem("pgp_pratiche_sortkey", sortKey);
    else localStorage.removeItem("pgp_pratiche_sortkey");
    localStorage.setItem("pgp_pratiche_sortdir", sortDir);
  }, [sortKey, sortDir]);
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const openDetail = (p) => { setDetail(p); setSearchParams({ p: p.id }); };
  useEffect(() => {
    const pid = searchParams.get("p");
    if (!pid) { setDetail(null); return; }
    if (rows.length && (!detail || String(detail.id) !== String(pid))) {
      const r = rows.find((x) => String(x.id) === String(pid));
      if (r) setDetail(r);
    }
  }, [searchParams, rows]);
  useEffect(() => {
    const s = searchParams.get("stato");
    if (s) setFStato(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  useEffect(() => { try { setHasDraft(!!localStorage.getItem("pgp_wizard_draft")); } catch { setHasDraft(false); } }, [wizardOpen]);
  const [genOpen, setGenOpen] = useState(false);
  const [genPratica, setGenPratica] = useState(null);
  const [operators, setOperators] = useState([]);
  const [dinamiche, setDinamiche] = useState({});
  const [dinamicaOpen, setDinamicaOpen] = useState(false);
  const [salvaDinOpen, setSalvaDinOpen] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [connResults, setConnResults] = useState([]);
  const [connOpen, setConnOpen] = useState(false);
  const [proCatalog, setProCatalog] = useState({});
  const [proDialog, setProDialog] = useState(null);
  const loadOneCatalog = async (atype) => { try { const { data } = await api.get(`/anagrafiche/${atype}`); setProCatalog((c) => ({ ...c, [atype]: data })); } catch {} };
  const loadProCatalog = () => PRO_ATYPES.forEach(loadOneCatalog);

  const doRenumber = async () => {
    const nuovo = (renumberVal || "").trim();
    const attuale = renumber?.numero_pratica || "";
    if (!nuovo) { toast.error("Inserisci un numero pratica"); return; }
    if (nuovo === attuale) { setRenumber(null); return; }
    if (rows.some((r) => r.id !== renumber.id && r.numero_pratica === nuovo)) {
      toast.error(`Il numero pratica ${nuovo} è già esistente`); return;
    }
    if (!window.confirm(`Stai per modificare il numero pratica da ${attuale} a ${nuovo}. Continuare?`)) return;
    setRenumberSaving(true);
    try {
      const { data } = await api.patch(`/pratiche/${renumber.id}/numero`, { numero_pratica: nuovo });
      setRows((rs) => rs.map((r) => (r.id === renumber.id ? { ...r, numero_pratica: data.numero_pratica } : r)));
      setDetail((d) => (d && d.id === renumber.id ? { ...d, numero_pratica: data.numero_pratica } : d));
      toast.success(`Numero pratica aggiornato a ${data.numero_pratica}`);
      setRenumber(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRenumberSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, c, o, d] = await Promise.all([
        api.get("/studio/pratiche"), api.get("/studio/clients"),
        api.get("/studio/operators").catch(() => ({ data: [] })),
        api.get("/studio/dinamiche").catch(() => ({ data: {} })),
      ]);
      setRows(p.data);
      setClients(c.data);
      setOperators(o.data || []);
      setDinamiche(d.data || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadProCatalog(); }, []);

  useEffect(() => {
    if (!rows.length) return;
    console.debug("[Comp.Gestionaria] Debug lettura compagnia per pratica:");
    console.table(rows.map((p) => {
      const tipo = (p.tipo || "").toUpperCase();
      return {
        numero: p.numero_pratica,
        tipo: p.tipo || "(vuoto → controparte)",
        fonte: tipo === "RD" ? "cliente (RD)" : "controparte",
        compagnia_cliente: clientCompany(p) || "(vuoto)",
        compagnia_controparte: controparteCompany(p) || "(vuoto)",
        mostrata: gestionariaCompany(p) || "(VUOTO)",
      };
    }));
  }, [rows]);

  const openNew = () => setWizardOpen(true);
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p, importo_risarcimento: p.importo_risarcimento ?? "", cliente_id: p.cliente_id || "none", data_sinistro: p.data_sinistro || "", ...(user?.demo_registered ? { utente: user.name || user.email } : {}) });
    setOpen(true);
  };

  const doSave = async () => {
    setSaving(true);
    const payload = {
      ...form,
      importo_risarcimento: parseFloat(form.importo_risarcimento) || 0,
      cliente_id: form.cliente_id === "none" ? null : form.cliente_id,
      data_sinistro: form.data_sinistro || null,
    };
    try {
      if (editing) await api.patch(`/studio/pratiche/${editing.id}`, payload);
      else await api.post("/studio/pratiche", payload);
      toast.success(editing ? "Pratica aggiornata" : "Pratica creata");
      setOpen(false);
      if (editing) setDetail((d) => (d && d.id === editing.id ? { ...d, ...payload } : d));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runQueue = (list) => {
    if (!list.length) { doSave(); return; }
    const pf = list[0];
    const rec = (proCatalog[pf.atype] || []).find((i) => displayName(i).toLowerCase() === (form[pf.key] || "").toLowerCase());
    setProDialog({ ...pf, initialName: form[pf.key], editRec: rec || null, queue: list });
  };

  const save = (e) => {
    e.preventDefault();
    const miss = PRO_FIELDS.filter((pf) => {
      const v = (form[pf.key] || "").trim();
      if (!v) return false;
      const items = proCatalog[pf.atype] || [];
      return !items.some((i) => displayName(i).toLowerCase() === v.toLowerCase());
    });
    if (miss.length) runQueue(miss); else doSave();
  };

  const handleProDone = (saved, rec) => {
    const pd = proDialog; setProDialog(null);
    if (saved && rec && pd) { const nm = displayName(rec); setForm((f) => ({ ...f, [pd.key]: nm })); loadOneCatalog(pd.atype); }
    if (pd?.queue) { const rest = pd.queue.slice(1); setTimeout(() => runQueue(rest), 80); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare la pratica ${p.numero_pratica}?`)) return;
    try {
      await api.delete(`/studio/pratiche/${p.id}`);
      toast.success("Pratica eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const moveToTrash = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      await api.post("/studio/pratiche/cestino/move", { ids });
      toast.success(`${ids.length} pratiche spostate nel cestino`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const archiviaBulk = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const { data } = await api.post("/studio/pratiche/archivia-bulk", { ids });
      if (data.skipped && data.skipped.length) toast.warning(`${data.archived} archiviate. Saltate (non chiuse): ${data.skipped.join(", ")}`);
      else toast.success(`${data.archived} pratiche archiviate`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fld = (k, label, opts = {}) => (
    <div className={opts.w2 ? "sm:col-span-2" : ""}>
      <Label className="text-slate-300">{label}{opts.required ? " *" : ""}</Label>
      {opts.pro ? (
        <ProSelect atype={opts.pro} label={label} value={form[k]} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} compagnia={form.compagnia} testId={`f-${k}`} />
      ) : opts.compagnia ? (
        <div className="mt-1.5"><CompagniaField value={form[k]} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} testId={`f-${k}`} /></div>
      ) : opts.picker ? (
        <SubjectPicker atype={opts.picker} label={label} value={form[k]} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} testId={`f-${k}`} />
      ) : opts.select ? (
        <SubjectSelect atype={opts.select} value={form[k]} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} testId={`f-${k}`} placeholder={opts.placeholder} />
      ) : opts.anag ? (
        <div className="mt-1.5">
          <AnagAutocomplete atype={opts.anag} value={form[k]} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} testId={`f-${k}`} placeholder={opts.placeholder} />
        </div>
      ) : opts.textarea ? (
        <Textarea value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} rows={opts.rows || 2} data-testid={`f-${k}`} className="mt-1.5 bg-slate-900/60 border-slate-700" />
      ) : opts.type === "date" ? (
        <div className="mt-1.5"><DateInput value={form[k]} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} testId={`f-${k}`} className="h-9 bg-slate-900/60 border-slate-700 text-slate-100" /></div>
      ) : (
        <Input type={opts.type || "text"} required={opts.required} step={opts.step} placeholder={opts.placeholder}
          value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
          data-testid={`f-${k}`} className={`mt-1.5 bg-slate-900/60 border-slate-700 ${opts.mono ? "font-mono" : ""}`} />
      )}
    </div>
  );

  const sel = (k, label, options, opts = {}) => (
    <div className={opts.w2 ? "sm:col-span-2" : ""}>
      <Label className="text-slate-300">{label}</Label>
      <Select value={form[k] || ""} onValueChange={(v) => setForm((f) => ({ ...f, [k]: v === "__none__" ? "" : v }))}>
        <SelectTrigger data-testid={`f-${k}`} className="mt-1.5 bg-slate-900/60 border-slate-700 text-slate-100 [&>svg]:opacity-100"><SelectValue placeholder="Seleziona" /></SelectTrigger>
        <SelectContent className="bg-[#121824] border-slate-700 text-slate-100 max-h-72">
          <SelectItem value="__none__">—</SelectItem>
          {options.map((o) => <SelectItem key={String(o.v ?? o)} value={String(o.v ?? o)}>{o.l ?? o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const searchConnessa = async (q) => {
    setForm((f) => ({ ...f, pratica_connessa: q }));
    if (!q || q.length < 2) { setConnResults([]); setConnOpen(false); return; }
    try {
      const { data } = await api.get(`/studio/pratiche-search?q=${encodeURIComponent(q)}`);
      setConnResults((data || []).filter((r) => r.id !== editing?.id));
      setConnOpen(true);
    } catch { setConnResults([]); }
  };

  const generaDinamicaAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/studio/dinamica-ai", { tipo: form.tipo, contesto: form.descrizione_sinistro });
      setForm((f) => ({ ...f, descrizione_sinistro: data.text, ...(data.responso ? { responso_dpr: data.responso } : {}) }));
      setAiGenerated(true);
      toast.success(data.responso ? `Dinamica generata · Responso: ${data.responso}` : "Dinamica generata con AI");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const norm = (s) => (s == null ? "" : String(s)).toLowerCase();
  const praticaYear = (p) => {
    const m = (p.numero_pratica || "").match(/^(\d{4})/);
    if (m) return parseInt(m[1], 10);
    if (p.created_at) { const y = new Date(p.created_at).getFullYear(); if (!isNaN(y)) return y; }
    return null;
  };
  const currentYear = new Date().getFullYear();
  const availableYears = [...new Set(rows.map(praticaYear).filter((y) => y != null))].sort((a, b) => b - a);
  const segNorm = (s) => foldText(s || "").replace(/[.,;:'"()\-_/]+/g, " ").trim().replace(/\s+/g, " ");
  const segOf = (p) => (p.assistito_scheda?.segnalatore || p.segnalatore || "").trim();
  const segOptions = (() => {
    const m = new Map();
    for (const p of rows) { const n = segOf(p); if (n && !m.has(segNorm(n))) m.set(segNorm(n), n); }
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1], "it"));
  })();
  const segRows = fSeg === "all" ? [] : rows.filter((p) => segNorm(segOf(p)) === fSeg);

  const searchTerms = foldText(query).split(/\s+/).filter(Boolean);
  const searchGroups = (p) => {
    const cps = Array.isArray(p.controparti) ? p.controparti : [];
    const cp = cps[0] || {};
    const g = [
      ["assistito", [assistitoName(p), p.nome_assistito, p.assistito_ragione_sociale, p.cliente_nome, p.assistito_scheda?.cognome, p.assistito_scheda?.nome, ...(Array.isArray(p.assistiti) ? p.assistiti.flatMap((a) => [a?.ragione_sociale, [a?.cognome, a?.nome].filter(Boolean).join(" ")]) : [])]],
      ["n° pratica", [p.numero_pratica]],
      ["controparte", cps.flatMap((c) => [c.cognome, c.nome, c.ragione_sociale, c.nominativo, [c.cognome, c.nome].filter(Boolean).join(" "), c.conducente])],
      ["targa", [p.targa, p.assistiti?.[0]?.targa, ...cps.map((c) => c.targa)]],
      ["compagnia", [gestionariaCompany(p), clientCompany(p), controparteCompany(p)]],
      ["liquidatore", [p.assistito_liquidatore, cp.liquidatore]],
      ["perito", [p.assistito_perito_compagnia, p.assistito_perito_parte, cp.perito]],
      ["n° sinistro", [gestionariaNumSinistro(p)]],
      ["altri campi", collectSearchText(p)],
    ];
    return g.map(([label, vals]) => [label, vals.filter(Boolean).map(foldText)]);
  };
  const termRes = searchTerms.map((t) => new RegExp(`(^|[^a-z0-9])${t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`));
  const baseRows = rows.filter((p) => {
    if (fStato !== "all" && p.stato !== fStato) return false;
    if (fTipo !== "all" && (p.tipo || "") !== fTipo) return false;
    if (fAnno === "last2") { const y = praticaYear(p); if (y != null && y < currentYear - 1) return false; }
    else if (fAnno !== "all") { if (String(praticaYear(p)) !== fAnno) return false; }
    if (fSeg !== "all" && segNorm(segOf(p)) !== fSeg) return false;
    return true;
  });
  const fieldsMatch = (fs) => {
    if (!fs.length) return false;
    if (searchTerms.length === 1) return fs.join(" | ").includes(searchTerms[0]);
    return fs.some((f) => termRes.every((re) => re.test(f))) || termRes.every((re) => fs.some((f) => re.test(f)));
  };
  const byNewest = (a, b) => (b.created_at || "").localeCompare(a.created_at || "");
  const { primaryRows, otherRows, matchWhere } = (() => {
    if (!searchTerms.length) return { primaryRows: [...baseRows].sort(byNewest), otherRows: [], matchWhere: {} };
    const prim = [], oth = [], where = {};
    for (const p of baseRows) {
      const groups = searchGroups(p);
      if (fieldsMatch(groups[0][1])) { prim.push(p); continue; }
      const hit = groups.slice(1).filter(([, fs]) => fieldsMatch(fs)).map(([l]) => l);
      const all = groups.slice(1).flatMap(([, fs]) => fs);
      if (hit.length || fieldsMatch(all)) {
        oth.push(p);
        const strict = hit.filter((l) => l !== "altri campi");
        const loose = groups.slice(1).filter(([l, fs]) => l !== "altri campi" && termRes.some((re) => fs.some((f) => re.test(f)))).map(([l]) => l);
        const named = strict.length ? strict : loose;
        where[p.id] = (named.length ? named : ["altri campi"]).join(", ");
      }
    }
    return { primaryRows: prim.sort(byNewest), otherRows: oth.sort(byNewest), matchWhere: where };
  })();
  const filteredRows = [...primaryRows, ...otherRows];

  const dateKey = (s) => {
    if (!s) return "";
    const m1 = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m1) return m1[1] + m1[2] + m1[3];
    const m2 = String(s).match(/^(\d{2})[/\-](\d{2})[/\-](\d{4})/);
    if (m2) return m2[3] + m2[2] + m2[1];
    return String(s);
  };
  const praticaKey = (s) => String(s || "")
    .split(/[^0-9A-Za-z]+/)
    .filter(Boolean)
    .map((seg) => (/^\d+$/.test(seg) ? seg.padStart(12, "0") : seg.toLowerCase()))
    .join("|");
  const sortVal = (p, key) => {
    switch (key) {
      case "numero_pratica": return praticaKey(p.numero_pratica);
      case "assistito": return norm(assistitoName(p));
      case "targa": return norm(p.targa || p.assistiti?.[0]?.targa);
      case "compagnia": return norm(gestionariaCompany(p));
      case "sinistro": return dateKey(p.data_sinistro || "");
      case "stato": return norm(STATI.find((s) => s.v === p.stato)?.l || p.stato);
      default: return "";
    }
  };
  const sortList = (list) => (sortKey
    ? [...list].sort((a, b) => {
        const va = sortVal(a, sortKey), vb = sortVal(b, sortKey);
        const c = va < vb ? -1 : va > vb ? 1 : 0;
        return sortDir === "asc" ? c : -c;
      })
    : list);
  const sortedPrimary = sortList(primaryRows);
  const displayRows = [...sortedPrimary, ...sortList(otherRows)];
  const firstOtherIdx = otherRows.length ? sortedPrimary.length : -1;
  const arrow = (key) => (sortKey === key ? (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : null);

  return (
    <div data-testid="pratiche-page">
      {detail ? (
        <PraticaDetail
          pratica={detail}
          backLabel={searchParams.get("ret")?.startsWith("/app/") ? "Torna a Gestione segnalatore" : undefined}
          onBack={() => {
            const ret = searchParams.get("ret");
            if (ret && ret.startsWith("/app/")) { navigate(ret); return; }
            setDetail(null); setSearchParams({});
          }}
          onEdit={() => openEdit(detail)}
          onDelete={() => { remove(detail); setDetail(null); }}
          onGenerate={() => { setGenPratica(detail); setGenOpen(true); }}
          onOpenPratica={(numero) => { const t = rows.find((r) => r.numero_pratica === numero); if (t) openDetail(t); else toast.error("Pratica connessa non trovata"); }}
          onDuplicated={(data) => { load(); openDetail(data); }}
          onArchived={() => { setDetail(null); setSearchParams({}); load(); }}
          onSaved={(updated) => { setDetail(updated); setRows((rs) => rs.map((r) => (r.id === updated.id ? { ...r, ...updated } : r))); }}
        />
      ) : (
      <div>
      <PageHeader
        title="Gestione Pratiche"
        subtitle="Anagrafica sinistri: assistito, targa, compagnia e data sinistro. Base per lettere e messaggi."
        icon={FolderKanban}
        actions={
          <Button onClick={openNew} data-testid="new-pratica-button" className="relative bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            <Plus className="h-4 w-4 mr-1" /> Nuova Pratica
            {hasDraft && (
              <span data-testid="bozza-in-sospeso-badge" title="C'è una bozza di pratica salvata: riprendila qui"
                className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#0a0d14] text-amber-300 text-[10px] font-semibold px-2 py-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" /> bozza in sospeso
              </span>
            )}
          </Button>
        }
      />

      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="pratiche-counters">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${STATO_STYLE.aperta}`} data-testid="counter-aperte">Aperte <b className="font-mono">{filteredRows.filter((p) => p.stato === "aperta").length}</b></span>
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${STATO_STYLE.chiusa}`} data-testid="counter-chiuse">Chiuse <b className="font-mono">{filteredRows.filter((p) => p.stato === "chiusa").length}</b></span>
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${STATO_STYLE.out}`} data-testid="counter-ss" title="Senza Seguito">S.S. <b className="font-mono">{filteredRows.filter((p) => p.stato === "out").length}</b></span>
        <span className="ml-auto text-xs text-slate-400" data-testid="counter-totale">Pratiche trovate: <b className="font-mono text-slate-200">{filteredRows.length}</b> di <b className="font-mono text-slate-200">{rows.length}</b></span>
      </div>

      <div className="pgp-card p-3 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="pratiche-filters">
        <div className="relative flex-1">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="pratiche-search"
            placeholder="Cerca in tutti i campi: assistito, targa, compagnia, liquidatore, perito controparte, n° sinistro…"
            className="bg-slate-900/60 border-slate-700" />
        </div>
        <Select value={fTipo} onValueChange={setFTipo}>
          <SelectTrigger data-testid="filter-tipo" className="w-full sm:w-40 bg-slate-900/60 border-slate-700 text-slate-100 [&>svg]:opacity-100"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-700 text-slate-100 max-h-72">
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {tipiSel.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStato} onValueChange={setFStato}>
          <SelectTrigger data-testid="filter-stato" className="w-full sm:w-44 bg-slate-900/60 border-slate-700 text-slate-100 [&>svg]:opacity-100"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {STATI.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fAnno} onValueChange={setFAnno}>
          <SelectTrigger data-testid="filter-anno" className="w-full sm:w-44 bg-slate-900/60 border-slate-700 text-slate-100 [&>svg]:opacity-100"><SelectValue placeholder="Anno" /></SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-700 text-slate-100 max-h-72">
            <SelectItem value="all">Tutte le pratiche</SelectItem>
            <SelectItem value="last2">Ultimi 2 anni</SelectItem>
            {availableYears.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fSeg} onValueChange={setFSeg}>
          <SelectTrigger data-testid="filter-segnalatore" className="w-full sm:w-52 bg-slate-900/60 border-slate-700 text-slate-100 [&>svg]:opacity-100"><SelectValue placeholder="Segnalatore" /></SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-700 text-slate-100 max-h-72">
            <SelectItem value="all">Tutti i segnalatori</SelectItem>
            {segOptions.map(([k, n]) => <SelectItem key={k} value={k}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        {(query || fTipo !== "all" || fStato !== "all" || fAnno !== "all" || fSeg !== "all") && (
          <Button variant="ghost" onClick={() => { setQuery(""); setFTipo("all"); setFStato("all"); setFAnno("all"); setFSeg("all"); }} data-testid="filter-reset" className="text-slate-400 hover:text-slate-100">Azzera</Button>
        )}
      </div>

      {fSeg !== "all" && <SegnalatoreTrendChart nome={(segOptions.find(([k]) => k === fSeg) || [])[1] || ""} pratiche={segRows} />}

      <div className="pgp-card overflow-hidden pgp-fade-up">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">Nessuna pratica. Creane una per iniziare.</div>
        ) : filteredRows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm" data-testid="pratiche-no-results">Nessuna pratica corrisponde ai filtri.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]" data-testid="pratiche-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wider font-mono border-b border-slate-800">
                {canTrash && (
                  <th className="px-2 xl:px-3 2xl:px-4 py-2.5 w-10">
                    <Checkbox
                      checked={displayRows.length > 0 && displayRows.every((p) => selected.has(p.id))}
                      onCheckedChange={(v) => setSelected(v ? new Set(displayRows.map((p) => p.id)) : new Set())}
                      data-testid="pratiche-select-all"
                    />
                  </th>
                )}
                <th className="px-2 xl:px-3 2xl:px-4 py-2.5 cursor-pointer select-none hover:text-slate-300" onClick={() => toggleSort("numero_pratica")} data-testid="sort-numero_pratica"><span className="inline-flex items-center gap-1">N. Pratica {arrow("numero_pratica")}</span></th>
                <th className="px-2 xl:px-3 2xl:px-4 py-2.5 hidden lg:table-cell">Tipo</th>
                <th className="px-2 xl:px-3 2xl:px-4 py-2.5 cursor-pointer select-none hover:text-slate-300" onClick={() => toggleSort("assistito")} data-testid="sort-assistito"><span className="inline-flex items-center gap-1">Assistito {arrow("assistito")}</span></th>
                <th className="px-2 xl:px-3 2xl:px-4 py-2.5 cursor-pointer select-none hover:text-slate-300 hidden xl:table-cell" onClick={() => toggleSort("targa")} data-testid="sort-targa"><span className="inline-flex items-center gap-1">Targa {arrow("targa")}</span></th>
                <th className="px-2 xl:px-3 2xl:px-4 py-2.5 cursor-pointer select-none hover:text-slate-300 hidden md:table-cell" onClick={() => toggleSort("compagnia")} data-testid="sort-compagnia"><span className="inline-flex items-center gap-1">Comp. Gestionaria {arrow("compagnia")}</span></th>
                <th className="px-2 xl:px-3 2xl:px-4 py-2.5 hidden 2xl:table-cell">N° Sx Comp.</th>
                <th className="px-2 xl:px-3 2xl:px-4 py-2.5 cursor-pointer select-none hover:text-slate-300 hidden lg:table-cell" onClick={() => toggleSort("sinistro")} data-testid="sort-sinistro"><span className="inline-flex items-center gap-1">Data Sx {arrow("sinistro")}</span></th>
                <th className="px-2 xl:px-3 2xl:px-4 py-2.5 cursor-pointer select-none hover:text-slate-300" onClick={() => toggleSort("stato")} data-testid="sort-stato"><span className="inline-flex items-center gap-1">Stato {arrow("stato")}</span></th>
                <th className="px-2 xl:px-3 2xl:px-4 py-2.5 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {displayRows.map((p, idx) => (
                <Fragment key={p.id}>
                {searchTerms.length > 0 && idx === 0 && primaryRows.length > 0 && (
                  <tr data-testid="search-section-assistito"><td colSpan={12} className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider text-amber-400 bg-amber-500/5">Risultati sull'assistito ({primaryRows.length})</td></tr>
                )}
                {idx === firstOtherIdx && (
                  <tr data-testid="search-section-altri"><td colSpan={12} className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider text-sky-400 bg-sky-500/5 border-t-2 border-slate-700">Trovati in altri campi ({otherRows.length})</td></tr>
                )}
                <tr className={`${idx % 2 === 1 ? "bg-slate-500/10 " : ""}hover:bg-slate-800/40 transition-colors cursor-pointer`} data-testid={`pratica-row-${p.id}`} onClick={() => openDetail(p)}>
                  {canTrash && (
                    <td className="px-2 xl:px-3 2xl:px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSel(p.id)} data-testid={`pratica-check-${p.id}`} />
                    </td>
                  )}
                  <td className="px-2 xl:px-3 2xl:px-4 py-2.5 font-mono text-amber-400">
                    <span className="inline-flex items-center gap-1.5">
                      {p.numero_pratica}
                      {p.mora_pec && <MoraPecIcon m={p.mora_pec} id={p.id} />}
                      {isAdmin && (
                        <button type="button" title="Rinumera pratica" data-testid={`renumber-${p.id}`}
                          onClick={(e) => { e.stopPropagation(); setRenumber(p); setRenumberVal(p.numero_pratica || ""); }}
                          className="text-slate-500 hover:text-amber-300 transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                      )}
                    </span>
                  </td>
                  <td className="px-2 xl:px-3 2xl:px-4 py-2.5 hidden lg:table-cell">
                    {p.tipo ? (
                      <Badge data-testid={`tipo-${p.id}`} style={tipiColors[p.tipo] ? { backgroundColor: tipiColors[p.tipo], color: contrastText(tipiColors[p.tipo]), borderColor: tipiColors[p.tipo] } : undefined} className={tipiColors[p.tipo] ? "border font-mono text-xs hover:opacity-100" : "border border-amber-500/40 bg-amber-500/15 text-amber-300 font-mono text-xs hover:opacity-100"}>{p.tipo}</Badge>
                    ) : <span className="text-slate-500">—</span>}
                  </td>
                  <td className="px-2 xl:px-3 2xl:px-4 py-2.5 font-medium text-slate-100">
                    <div data-testid={`assistito-name-${p.id}`}>{assistitoName(p) ? <Hl text={assistitoName(p)} terms={searchTerms} /> : "—"}</div>
                    {matchWhere[p.id] && (
                      <div className="mt-0.5"><span data-testid={`match-where-${p.id}`} className="inline-block text-[10px] font-semibold text-sky-300 bg-sky-500/10 border border-sky-500/30 rounded px-1.5 py-px">trovato in: {matchWhere[p.id]}</span></div>
                    )}
                    {p.assistito_scheda?.cliente && (
                      <div className="mt-0.5"><span data-testid={`lista-cliente-badge-${p.id}`} className="inline-block text-[8px] font-bold uppercase tracking-wide text-sky-400/80 border border-sky-500/30 rounded px-1 py-px leading-tight">CLIENTE</span></div>
                    )}
                    <div className="text-xs font-normal text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap" data-testid={`ruolo-${p.id}`}>
                      <span>{p.assistito_ruolo || p.assistiti?.[0]?.ruolo || "—"}</span>
                      {p.assistito_scheda?.definita && <span data-testid={`lista-definita-badge-${p.id}`} className={`inline-block text-[9px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 ${defTipo(p.assistito_scheda).cls}`}>{defLabel(p.assistito_scheda)}</span>}
                    </div>
                    {(Array.isArray(p.assistiti) ? p.assistiti.slice(1) : [])
                      .filter((a) => a && a.cliente && (a.ragione_sociale || a.nome || a.cognome))
                      .map((a, ai) => (
                        <div key={ai} className="mt-1.5 pt-1.5 border-t border-slate-800/70" data-testid={`lista-extra-cliente-${p.id}-${ai}`}>
                          <div><Hl text={a.ragione_sociale || [a.cognome, a.nome].filter(Boolean).join(" ")} terms={searchTerms} /></div>
                          <div className="mt-0.5"><span className="inline-block text-[8px] font-bold uppercase tracking-wide text-sky-400/80 border border-sky-500/30 rounded px-1 py-px leading-tight">CLIENTE</span></div>
                          {(a.ruolo || a.definita) && (
                            <div className="text-xs font-normal text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                              <span>{a.ruolo || "—"}</span>
                              {a.definita && <span className={`inline-block text-[9px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 ${defTipo(a).cls}`}>{defLabel(a)}</span>}
                            </div>
                          )}
                        </div>
                      ))}
                  </td>
                  <td className="px-2 xl:px-3 2xl:px-4 py-2.5 font-mono text-slate-300 hidden xl:table-cell">{(p.targa || p.assistiti?.[0]?.targa) ? <Hl text={p.targa || p.assistiti?.[0]?.targa} terms={searchTerms} /> : "—"}</td>
                  <td className="px-2 xl:px-3 2xl:px-4 py-2.5 hidden md:table-cell" data-testid={`compagnia-cell-${p.id}`}>
                    {gestionariaCompany(p) ? (
                      <span data-testid={`compagnia-${p.id}`} title={gestionariaCompany(p)} className="inline-block max-w-[140px] lg:max-w-[180px] xl:max-w-[240px] truncate rounded-md border border-sky-300 bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-900 align-middle"><Hl text={gestionariaCompany(p)} terms={searchTerms} /></span>
                    ) : <span className="text-slate-500">—</span>}
                  </td>
                  <td className="px-2 xl:px-3 2xl:px-4 py-2.5 font-mono text-slate-300 text-xs hidden 2xl:table-cell" data-testid={`num-sinistro-comp-${p.id}`}><span className="inline-flex items-center gap-1.5">{gestionariaNumSinistro(p) || "—"}</span></td>
                  <td className="px-2 xl:px-3 2xl:px-4 py-2.5 text-slate-400 font-mono text-xs hidden lg:table-cell">{formatDate(p.data_sinistro)}</td>
                  <td className="px-2 xl:px-3 2xl:px-4 py-2.5">
                    <Badge className={`border ${STATO_STYLE[p.stato] || STATO_STYLE.aperta} hover:opacity-100`}>
                      {STATI.find((s) => s.v === p.stato)?.l || p.stato}
                    </Badge>
                  </td>
                  <td className="px-2 xl:px-3 2xl:px-4 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => { setGenPratica(p); setGenOpen(true); }} data-testid={`gen-${p.id}`} className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10">
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openDetail(p)} data-testid={`edit-${p.id}`} className="text-slate-400 hover:text-slate-100 hover:bg-slate-700/40">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`del-${p.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      {renumber && (
        <Dialog open onOpenChange={(o) => { if (!o) setRenumber(null); }}>
          <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-md" data-testid="renumber-dialog">
            <DialogHeader><DialogTitle className="font-head">Rinumera pratica</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-400">Numero attuale: <span className="font-mono text-amber-400">{renumber.numero_pratica || "—"}</span></p>
              <div>
                <Label className="text-slate-300 text-xs">Nuovo numero pratica</Label>
                <Input value={renumberVal} onChange={(e) => setRenumberVal(e.target.value)} data-testid="renumber-input" className="mt-1.5 h-9 bg-slate-900/60 border-slate-700 font-mono" />
              </div>
              <p className="text-[11px] text-slate-500">La modifica aggiornerà il numero in tutta l'app (lettere, allegati, comunicazioni collegate).</p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRenumber(null)} data-testid="renumber-cancel" className="border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</Button>
              <Button onClick={doRenumber} disabled={renumberSaving} data-testid="renumber-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{renumberSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0d1220] border-t border-slate-700 px-6 py-4 flex items-center justify-between shadow-2xl" data-testid="pratiche-action-bar">
          <span className="text-slate-200 font-medium" data-testid="pratiche-selection-counter">{selected.size} pratiche selezionate</span>
          <div className="flex items-center gap-2">
            <Button onClick={archiviaBulk} data-testid="pratiche-archive-btn" className="bg-purple-600 hover:bg-purple-700 text-white font-semibold">
              <Archive className="h-4 w-4 mr-1.5" /> Archivia selezionate
            </Button>
            {canTrash && (
              <Button onClick={moveToTrash} data-testid="pratiche-move-trash-btn" className="bg-red-600 hover:bg-red-700 text-white font-semibold">
                <Trash2 className="h-4 w-4 mr-1.5" /> Sposta nel Cestino
              </Button>
            )}
          </div>
        </div>
      )}
      </div>
      )}

      {/* Create / edit dialog */}
      <NuovaPraticaWizard open={wizardOpen} onOpenChange={setWizardOpen} operators={operators} onCreated={() => load()} onOpenCreated={(data) => { load(); openDetail(data); }} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editing ? "Modifica pratica" : "Nuova pratica"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-6" data-testid="pratica-form">
            <div className="grid sm:grid-cols-2 gap-4">
              {fld("nome_assistito", "Nome assistito", { required: true, w2: true })}
              {fld("numero_pratica", "N° Pratica", { mono: true, placeholder: "auto (NNNN/AAAA/CC)" })}
              {sel("tipo", "Tipo", tipiSel)}
              {sel("utente", "Utente", operators.map((o) => ({ v: o.name || o.email, l: o.name || o.email })))}
              {fld("fornitore_pratica", "Fornitore pratica")}
              <div>
                <Label className="text-slate-300">Pratica connessa</Label>
                <div className="relative mt-1.5">
                  <Input value={form.pratica_connessa || ""} onChange={(e) => searchConnessa(e.target.value)} onFocus={() => connResults.length && setConnOpen(true)} placeholder="Cerca per n° pratica…" data-testid="f-pratica_connessa" className="bg-slate-900/60 border-slate-700 pr-8" />
                  <Link2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  {connOpen && connResults.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-lg max-h-52 overflow-y-auto" data-testid="conn-results">
                      {connResults.map((r) => (
                        <button key={r.id} type="button" onClick={() => { setForm((f) => ({ ...f, pratica_connessa: r.numero_pratica })); setConnOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/60">
                          <span className="font-mono text-amber-400">{r.numero_pratica}</span> · {r.nome_assistito}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {fld("segnalatore", "Segnalatore", { select: "segnalatori" })}
              {fld("veicoli_coinvolti", "Veicoli coinvolti", { type: "number" })}
              {fld("data_sinistro", "Data sinistro", { type: "date" })}
              {fld("ora_sinistro", "Orario sinistro", { type: "time" })}
              {fld("luogo_sinistro", "Località sinistro", { anag: "comuni", placeholder: "Comune…" })}
              {fld("indirizzo_sinistro", "Indirizzo sinistro", { placeholder: "Via, piazza…" })}
              {sel("autorita_intervenute", "Autorità intervenute", AUTORITA)}
              {fld("stato_aggiuntivo", "Stato aggiuntivo")}
              {sel("stato", "Stato", STATI)}
              {sel("responso_dpr", "Responso D.P.R.", RESPONSO_DPR)}
              {fld("codice_fiscale", "Codice fiscale", { mono: true })}
              {fld("importo_risarcimento", "Importo risarcimento (€)", { type: "number", step: "0.01", mono: true })}
              <div className="sm:col-span-2">
                <Label className="text-slate-300">Cliente collegato</Label>
                <Select value={form.cliente_id} onValueChange={(v) => setForm({ ...form, cliente_id: v })}>
                  <SelectTrigger data-testid="f-cliente" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                    <SelectItem value="none">Nessuno</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">Descrizione sinistro / Dinamica</Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => setDinamicaOpen(true)} data-testid="scegli-dinamica-btn" className="h-7 border-slate-700 text-slate-200 hover:bg-slate-800">Scegli dinamica</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setSalvaDinOpen(true)} data-testid="salva-dinamica-btn" className="h-7 border-slate-700 text-slate-200 hover:bg-slate-800">Salva dinamica</Button>
                    {aiGenerated && (
                      <Button type="button" size="sm" onClick={() => setSalvaDinOpen(true)} data-testid="salva-dinamica-ai-btn" className="h-7 bg-violet-600 hover:bg-violet-700 text-white"><Sparkles className="h-3.5 w-3.5 mr-1" /> Salva dinamica AI</Button>
                    )}
                    <Button type="button" size="sm" onClick={generaDinamicaAI} disabled={aiLoading} data-testid="ai-dinamica-btn" className="h-7 bg-violet-600 hover:bg-violet-700 text-white">
                      {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Sparkles className="h-3.5 w-3.5 mr-1" /> AI</>}
                    </Button>
                  </div>
                </div>
                <Textarea value={form.descrizione_sinistro || ""} onChange={(e) => setForm((f) => ({ ...f, descrizione_sinistro: e.target.value }))} rows={4} data-testid="f-descrizione_sinistro" className="mt-1.5 bg-slate-900/60 border-slate-700" />
              </div>
            </div>

            <section>
              <div className="text-sm font-head font-bold text-amber-400 tracking-wide mb-3">ASSISTITO</div>
              <div className="grid sm:grid-cols-2 gap-4">
                {fld("assistito_ragione_sociale", "Ragione sociale", { placeholder: form.nome_assistito })}
                {fld("assistito_ruolo", "Ruolo")}
                {fld("targa", "Targa", { mono: true })}
                {fld("compagnia", "Compagnia", { compagnia: true })}
                {fld("assistito_ispettorato", "Ispettorato / Sede")}
                <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2.5">
                  <Label className="text-amber-300 font-semibold">Numero di Sinistro</Label>
                  <Input value={form.assistito_num_sinistro || ""} onChange={(e) => setForm((f) => ({ ...f, assistito_num_sinistro: e.target.value }))} onBlur={(e) => warnDuplicateSinistro(e.target.value)} data-testid="f-assistito_num_sinistro" placeholder="Es. 123456/2026" className="mt-1.5 h-9 bg-white border-amber-500/60 text-black font-mono placeholder:text-slate-400" />
                </div>
                {PRO_FIELDS.map((pf) => (
                  <div key={pf.key}>
                    <Label className="text-slate-300">{pf.label}</Label>
                    <ProField items={proCatalog[pf.atype] || []} value={form[pf.key] || ""} onChange={(v) => setForm((f) => ({ ...f, [pf.key]: v }))} compagnia={form.assistito_compagnia || form.compagnia || ""} label={pf.label} testId={`f-${pf.key}`}
                      onEdit={() => { const rec = (proCatalog[pf.atype] || []).find((i) => displayName(i).toLowerCase() === (form[pf.key] || "").toLowerCase()); setProDialog({ ...pf, initialName: form[pf.key], editRec: rec || null }); }} />
                  </div>
                ))}
                {fld("assistito_istruttore", "Istruttore", { select: "istruttori" })}
              </div>
            </section>

            <section>
              <div className="text-sm font-head font-bold text-cyan-400 tracking-wide mb-2">CONTROPARTI</div>
              <p className="text-xs text-slate-500">Le controparti si gestiscono dalla scheda pratica: apri la pratica e usa il riquadro CONTROPARTI (Aggiungi / Modifica / Cancella). Puoi inserirne più di una.</p>
            </section>

            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="submit-pratica" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Salva modifiche" : "Crea pratica"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail: rendered inline above via PraticaDetail */}

      <Dialog open={dinamicaOpen} onOpenChange={setDinamicaOpen}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg max-h-[80vh] overflow-y-auto" data-testid="dinamica-dialog">
          <DialogHeader><DialogTitle className="font-head">Scegli dinamica {form.tipo ? `· ${form.tipo}` : ""}</DialogTitle></DialogHeader>
          {!form.tipo ? (
            <p className="text-sm text-slate-400 py-4">Seleziona prima il <b>Tipo</b> di sinistro per vedere le dinamiche disponibili.</p>
          ) : ((dinamiche[form.tipo] || []).length === 0) ? (
            <p className="text-sm text-slate-400 py-4">Nessuna dinamica preimpostata per questo tipo. Il titolare può aggiungerle da Impostazioni.</p>
          ) : (
            <div className="space-y-2">
              {(dinamiche[form.tipo] || []).map((t, i) => (
                <button key={i} type="button" onClick={() => { setForm((f) => ({ ...f, descrizione_sinistro: t })); setDinamicaOpen(false); }} data-testid={`dinamica-opt-${i}`} className="w-full text-left p-3 rounded-md border border-slate-700 text-sm text-slate-200 hover:border-amber-500/40 hover:bg-slate-800/50 transition-colors">
                  {t}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <NuovoProfessionistaDialog open={!!proDialog} atype={proDialog?.atype} label={proDialog?.label} initialName={proDialog?.initialName} editRec={proDialog?.editRec} onCancel={() => handleProDone(false)} onSaved={(rec) => handleProDone(true, rec)} />

      <GenerateLetterDialog open={genOpen} onOpenChange={setGenOpen} presetPraticaId={genPratica?.id} />
      <SalvaDinamicaDialog open={salvaDinOpen} onOpenChange={setSalvaDinOpen} testo={form.descrizione_sinistro || ""} tipo={form.tipo || ""} onSaved={() => api.get("/studio/dinamiche").then(({ data }) => setDinamiche(data || {})).catch(() => {})} />
    </div>
  );
}
